/**
 * Ekspert bahosi — javob ostidagi panel.
 *
 * === Nega chat oynasining o'zida ===
 *
 * Hozir har bir foydalanuvchi ekspert. Baho javob KO'RILGAN paytda,
 * kontekst yodda turganida berilsa to'g'ri bo'ladi; alohida panelga
 * o'tib baholash esa amalda hech qachon bajarilmaydi.
 *
 * "Xato" bosilganda to'g'ri kod so'raladi. Aynan shu maydon o'lchov
 * beradi: "yomon javob" — shikoyat, "3917 21 emas, 3917 32 bo'lishi
 * kerak edi" — tuzatish uchun material.
 */

import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Check, MessageSquare, ThumbsDown, ThumbsUp, HelpCircle } from "lucide-react";
import * as api from "../api/client";

const ISSUES = [
  ["kod", "Kod noto'g'ri"],
  ["tushuntirish", "Tushuntirish chalkash"],
  ["manba", "Manba yo'q / mos emas"],
  ["toliqmas", "To'liq javob emas"],
  ["boshqa", "Boshqa"],
];

const BTN =
  "flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-[11px] transition-colors";

export function Feedback({ turnId, code = "", rated = false }) {
  const [open, setOpen] = useState(false);      // batafsil forma ochiqmi
  const [verdict, setVerdict] = useState("");
  const [issue, setIssue] = useState("");
  const [correct, setCorrect] = useState("");
  const [comment, setComment] = useState("");
  // `rated` — tarixdan ochilgan javob avval baholangan
  const [saved, setSaved] = useState(rated ? "old" : false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Baho yozib bo'lmasa (baza yo'q) tugmalarni ko'rsatish — ekspert
  // vaqtini behuda sarflash.
  if (!turnId) return null;

  const submit = async (v, extra = {}) => {
    setBusy(true);
    setError("");
    try {
      await api.sendFeedback({
        turn_id: turnId,
        verdict: v,
        correct_code: extra.correct ?? "",
        comment: extra.comment ?? "",
        issue: extra.issue ?? "",
      });
      setSaved(true);
      setOpen(false);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const pick = (v) => {
    setVerdict(v);
    setError("");
    // "To'g'ri" — bir bosishda tugaydi. Ekspertdan qo'shimcha ma'lumot
    // so'rash uni baho berishdan qaytaradi.
    if (v === "togri") submit("togri");
    else setOpen(true);
  };

  if (saved) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex items-center gap-1.5 text-[11px] text-emerald-300"
      >
        <Check className="h-3.5 w-3.5 text-emerald-400" />
        <span>{saved === "old" ? "Baholangan" : "Baho yozildi — rahmat"}</span>
      </motion.div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] text-sky-300/70">Javob to'g'rimi?</span>

        <button
          onClick={() => pick("togri")}
          disabled={busy}
          className={`${BTN} border-emerald-800/50 bg-emerald-950/40 text-emerald-200 hover:bg-emerald-900/50 disabled:opacity-50`}
        >
          <ThumbsUp className="h-3.5 w-3.5" />
          <span>To'g'ri</span>
        </button>

        <button
          onClick={() => pick("xato")}
          disabled={busy}
          className={`${BTN} border-rose-800/50 bg-rose-950/40 text-rose-200 hover:bg-rose-900/50 disabled:opacity-50`}
        >
          <ThumbsDown className="h-3.5 w-3.5" />
          <span>Xato</span>
        </button>

        <button
          onClick={() => pick("noaniq")}
          disabled={busy}
          className={`${BTN} border-amber-800/50 bg-amber-950/40 text-amber-200 hover:bg-amber-900/50 disabled:opacity-50`}
        >
          <HelpCircle className="h-3.5 w-3.5" />
          <span>Noaniq</span>
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="space-y-2.5 rounded-xl border border-sky-800/40 bg-[#030f24]/90 p-3">
              <div className="flex flex-wrap gap-1.5">
                {ISSUES.map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setIssue(issue === key ? "" : key)}
                    className={`rounded-lg border px-2 py-0.5 text-[10px] transition-colors ${
                      issue === key
                        ? "border-sky-400/60 bg-sky-900/70 text-white"
                        : "border-sky-800/50 bg-[#041228] text-sky-300 hover:bg-sky-900/40"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/*
                To'g'ri kod — panelning eng qimmatli maydoni. Server uni
                nomenklaturada tekshiradi, shuning uchun xato yozilgan
                kod jimgina o'lchovga kirmaydi.
              */}
              <div className="flex flex-wrap items-center gap-2">
                <label className="text-[11px] text-sky-300/70">
                  To'g'ri kod:
                </label>
                <input
                  value={correct}
                  onChange={(e) => setCorrect(e.target.value)}
                  placeholder={code || "3917 21"}
                  inputMode="numeric"
                  className="w-32 rounded-lg border border-sky-800/50 bg-[#041228] px-2 py-1 font-mono text-[11px] text-white placeholder:text-sky-700 focus:border-sky-500 focus:outline-none"
                />
                <span className="text-[10px] text-sky-500/50">
                  4-10 raqam, ixtiyoriy
                </span>
              </div>

              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={2}
                placeholder="Nima noto'g'ri? Qisqacha izoh..."
                className="w-full resize-none rounded-lg border border-sky-800/50 bg-[#041228] px-2 py-1.5 text-[11px] text-white placeholder:text-sky-700 focus:border-sky-500 focus:outline-none"
              />

              {error && (
                <div role="alert" className="text-[11px] text-rose-300">
                  {error}
                </div>
              )}

              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    submit(verdict, {
                      correct: correct.trim(),
                      comment: comment.trim(),
                      issue,
                    })
                  }
                  disabled={busy}
                  className="flex items-center gap-1.5 rounded-lg border border-sky-500/40 bg-sky-900/60 px-3 py-1 text-[11px] font-medium text-white transition-colors hover:bg-sky-800/70 disabled:opacity-50"
                >
                  <MessageSquare className="h-3.5 w-3.5" />
                  <span>{busy ? "Yozilmoqda..." : "Yuborish"}</span>
                </button>
                <button
                  onClick={() => setOpen(false)}
                  className="rounded-lg px-2 py-1 text-[11px] text-sky-400/70 transition-colors hover:text-sky-200"
                >
                  Bekor qilish
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
